import { Transaction } from "firebase/firestore"
import _ from "lodash"
import { useCallback, useState } from "react"

import Firebase from "#classes/Firebase"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  type DOC_TYPES,
  type FirebaseBatchUpdate,
  type FirebaseBatchUpdateResponse,
} from "#customTypes/composedTypes/firebaseTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { GenericObject, invariant } from "#customTypes/types"
import { ErrorType, formatError, stringify } from "#utils/textFormatters"

interface BatchUpdateReturn {
  mutate: (data: FirebaseBatchUpdate) => Promise<FirebaseBatchUpdateResponse>
  isLoading: boolean
  error: Error | null
}

interface BatchUpdateDynamicReturn {
  mutate: (
    data: FirebaseBatchUpdate,
    docTypes: DOC_TYPES[]
  ) => Promise<FirebaseBatchUpdateResponse>
  isLoading: boolean
  error: Error | null
}

// onFinish handles errors!
export function useFirebaseBatchUpdate(
  docTypes: DOC_TYPES[],
  onFinish?: (error?: ErrorType) => void
): BatchUpdateReturn {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { currentUserId } = useUserContext()

  const executeBatchUpdate = useCallback(
    async (data: FirebaseBatchUpdate): Promise<FirebaseBatchUpdateResponse> => {
      setIsLoading(true)
      setError(null)

      try {
        if (!isManagerLoggedIn) throw new Error("Not logged in!")

        // Make sure docTypes array matches data docType keys
        invariant(
          _.xor(docTypes, Object.keys(data)).length === 0,
          `Mismatch between expected and submitted docTypes for ${stringify(
            docTypes
          )}`
        )

        const result = await Firebase.getInstance().updateDocuments(data)

        // Let Firebase listeners handle cache updates
        // This prevents double-updates and unnecessary re-renders
        onFinish?.()

        setIsLoading(false)
        return result
      } catch (err) {
        const error = new Error(formatError(err as ErrorType))
        setError(error)
        setIsLoading(false)
        throw error
      }
    },
    [docTypes, isManagerLoggedIn, onFinish, currentUserId]
  )

  return {
    mutate: executeBatchUpdate,
    isLoading,
    error,
  }
}

/**
 * Batch update where the set of doc types is declared at call time instead of hook init.
 * Use when the payload varies per submit (e.g. only some doc types are touched).
 * Invariant: docTypes must exactly match Object.keys(data).
 */
export function useFirebaseBatchUpdateDynamic(
  onFinish?: (error?: ErrorType) => void
): BatchUpdateDynamicReturn {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { currentUserId } = useUserContext()

  const executeBatchUpdate = useCallback(
    async (
      data: FirebaseBatchUpdate,
      docTypes: DOC_TYPES[]
    ): Promise<FirebaseBatchUpdateResponse> => {
      setIsLoading(true)
      setError(null)

      const payloadSummary = _.mapValues(data, (rec) => Object.keys(rec ?? {}))
      console.log("[useFirebaseBatchUpdateDynamic] mutate called", {
        docTypes,
        payloadSummary,
        keysMatch: _.isEqual(_.sortBy(docTypes), _.sortBy(Object.keys(data))),
      })

      try {
        if (!isManagerLoggedIn) throw new Error("Not logged in!")

        invariant(
          _.xor(docTypes, Object.keys(data)).length === 0,
          `Mismatch between expected and submitted docTypes for ${stringify(
            docTypes
          )}`
        )
        console.log("[useFirebaseBatchUpdateDynamic] invariant passed")

        const result = await Firebase.getInstance().updateDocuments(data)
        console.log("[useFirebaseBatchUpdateDynamic] updateDocuments result", {
          updatedIdsByType: _.mapValues(result, (ids) => ids?.length ?? 0),
        })

        onFinish?.()
        setIsLoading(false)
        return result
      } catch (err) {
        console.error("[useFirebaseBatchUpdateDynamic] failed", err)
        const error = new Error(formatError(err as ErrorType))
        setError(error)
        setIsLoading(false)
        throw error
      }
    },
    [isManagerLoggedIn, onFinish, currentUserId]
  )

  return {
    mutate: executeBatchUpdate,
    isLoading,
    error,
  }
}

export interface TransactionData {
  docType: DOC_TYPES
  id: Uuid
  data: GenericObject | null
  action: "create" | "update" | "delete"
}

interface TransactionReturn {
  mutate: (data: TransactionData[]) => Promise<void>
  isLoading: boolean
  error: Error | null
}

/**
 * Commits the given writes atomically, but performs no reads inside the
 * transaction. Values computed from subscribed/atom data before calling
 * `mutate` can be stale — when a write depends on current doc state, put the
 * read and the write in one `Firebase.runTransaction` instead.
 */
export function useFirebaseTransaction(
  onFinish?: (error?: Error) => void
): TransactionReturn {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const executeTransaction = useCallback(
    async (data: TransactionData[]): Promise<void> => {
      setIsLoading(true)
      setError(null)

      try {
        if (!isManagerLoggedIn) throw new Error("Not logged in!")

        await Firebase.getInstance().runTransaction<void>(
          async (transaction: Transaction) => {
            data.forEach((item: TransactionData) => {
              const ref = Firebase.getInstance().getRefById(
                item.docType,
                item.id
              )

              switch (item.action) {
                case "create":
                  invariant(
                    item.data != null,
                    `Missing data for creating ${item.docType} ${item.id}`
                  )
                  transaction.set(ref, item.data)
                  break
                case "update":
                  invariant(
                    item.data != null,
                    `Missing data for updating ${item.docType} ${item.id}`
                  )
                  transaction.update(ref, item.data)
                  break
                case "delete":
                  transaction.delete(ref)
                  break
              }
            })
          }
        )

        // Let Firebase listeners handle cache updates
        onFinish?.()
        setIsLoading(false)
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        setError(error)
        setIsLoading(false)
        onFinish?.(error)
        throw error
      }
    },
    [isManagerLoggedIn, onFinish]
  )

  return {
    mutate: executeTransaction,
    isLoading,
    error,
  }
}
